import { useGlobal } from 'hooks/useGlobal';
import { FC, memo, RefObject, useEffect, useRef, useState } from 'react';
import { useConditionalEffect, usePrevious } from 'react-delta';
import { cloneDeep, isNumber } from 'lodash';
import Highcharts, {
  Chart,
  ChartSelectionContextObject,
  Point,
  PointerEventObject,
  SeriesXrangeOptions,
  XrangePointOptionsObject,
} from 'highcharts';
import { useRecoilValue, useSetRecoilState } from 'recoil';

import * as K from 'common/constants';
import { addCommas } from 'common/util/number';
import ScalyrHighcharts from 'components/common/ScalyrHighcharts';
import { distributionYScaleState } from 'components/search/DistributionPage/DistributionYScaleDropdown';
import { currentChartRefState } from 'components/common/Graphs/graphs.state';

import { distributionGraphStylesState, highchartsStylesState } from 'styles/stylesProvider';
import './DistributionGraph.scss';

type DistributionGraphProps = {
  xValues: Array<number>;
  yValues: Array<number>;
  className: string;
};

type HighchartsPoint = Point & {
  x: number;
  chartX: number;
};

type ContainerPropEvent = Event & {
  nativeEvent: PointerEventObject;
};

interface ExtendedAxisOptions extends Highcharts.XAxisOptions {
  custom?: {
    allowNegativeLog: boolean;
  };
}

interface ExtendedAxis extends Highcharts.Axis {
  logarithmic: {
    log2lin: (num: number) => number;
    lin2log: (num: number) => number;
  };
  options: ExtendedAxisOptions;
  positiveValuesOnly: boolean;
}

Highcharts.addEvent(Highcharts.Axis as unknown as ExtendedAxis, 'afterInit', function AxisAfterInit() {
  const logarithmic = this.logarithmic;

  if (logarithmic && this.options.custom?.allowNegativeLog) {
    // Avoid errors on negative numbers on a log axis
    this.positiveValuesOnly = false;

    // To fit a zero on a logarithmic scale that usually spans 1 to 10 to 100 and so on, we need to compress first interval to fit the 0 value. The constant determines length of interval to compress.
    const compressionInterval = 10;

    // Override the converter functions to compress an interval and fit an extra value on it
    logarithmic.log2lin = (num: number): number => {
      const isNegative = num < 0;

      let adjustedNum = Math.abs(num);

      if (adjustedNum < compressionInterval) {
        adjustedNum += (compressionInterval - adjustedNum) / compressionInterval;
      }

      const result = Math.log(adjustedNum) / Math.LN10;
      return isNegative ? -result : result;
    };

    logarithmic.lin2log = (num: number): number => {
      const isNegative = num < 0;

      let result = compressionInterval ** Math.abs(num);
      if (result < compressionInterval) {
        result = (compressionInterval * (result - 1)) / (compressionInterval - 1);
      }
      return isNegative ? -result : result;
    };
  }
});

const DistributionGraph: FC<DistributionGraphProps> = ({ xValues, yValues, className }: DistributionGraphProps) => {
  const [selectionRange, setSelectionRange] = useGlobal(K.GLOBAL_KEY.CHART_SELECTION_RANGE);
  const setCurrentChartRef = useSetRecoilState(currentChartRefState);
  const distributionYScale = useRecoilValue(distributionYScaleState);
  const distributionGraphStyles = useRecoilValue(distributionGraphStylesState);
  const mouseDownRef = useRef<number | null>(null);
  const data = xValues.map((el: number, i: number): XrangePointOptionsObject => ({ x: el, y: yValues[i] }));
  const highchartsStyles = useRecoilValue(highchartsStylesState);

  const chartRef = useRef<{ chart: Chart; container: RefObject<HTMLDivElement> }>(null); // used for exporting our chart

  const calculateDataInSelection = (min: number, max: number): number => {
    const indexes: Array<number> = xValues.reduce<number[]>((accum, value, index) => {
      if (value >= min && value >= max) {
        return [...accum, index];
      }
      return accum;
    }, []);
    let sum = 0;
    for (let index = indexes[0]; index <= indexes.slice(-1)[0]; index += 1) {
      sum += yValues[index];
    }
    return sum;
  };

  useEffect(() => {
    if (selectionRange?.selectionBox) {
      chartRef?.current?.chart.axes[0].addPlotBand({
        className: 'DistributionGraph__PlotBand',
        from: selectionRange?.start,
        to: selectionRange?.end,
        zIndex: K.CHART_SELECTION_ZINDEX,
        id: K.CHART_SELECTION_ID,
      });
    } else {
      chartRef?.current?.chart.axes[0].removePlotBand(K.CHART_SELECTION_ID);
    }
  }, [selectionRange]);

  const [chartOptions, setChartOptions] = useState<Highcharts.Options>({
    chart: {
      type: 'column',
      margin: [50, 50, 50, 50],
      zoomType: 'x',
      animation: false,
      events: {
        click(): void {
          setSelectionRange({ ...selectionRange, selectionBox: false });
        },
        selection: (e: ChartSelectionContextObject): boolean | undefined => {
          if (e?.xAxis) {
            const selectionAxis = e.xAxis[0];
            const start = selectionAxis.min;
            const end = selectionAxis.max;
            const sum = calculateDataInSelection(start, end);
            setSelectionRange({
              start,
              end,
              sum,
              selectionBox: true,
            });
          }
          return false;
        },
      },
    },
    legend: {
      enabled: false,
    },
    credits: {
      enabled: false,
    },
    exporting: {
      enabled: false,
    },
    tooltip: {
      enabled: true,
      shared: true,
      followPointer: true,
      formatter(): string {
        // this used for context to which this function does have access
        // eslint-disable-next-line react/no-this-in-sfc
        return `Value: ${addCommas(this.x, undefined, -1)}  <br/>Count: ${addCommas(this.y, undefined, -1)}`;
      },
    },
    title: {
      text: '',
      style: {
        display: 'none',
      },
    },
    subtitle: {
      text: '',
      style: {
        display: 'none',
      },
    },
    plotOptions: {
      column: {
        pointPadding: 0,
        borderWidth: 0,
        groupPadding: 0,
        shadow: false,
      },
      series: {
        point: {
          events: {
            mouseOver(e: Event): void {
              const target = e.target as unknown as HighchartsPoint;
              // if we have a mouseDownRef, then user is currently making a selection, so capture start and end.
              if (isNumber(mouseDownRef?.current) && isNumber(target?.x)) {
                setSelectionRange({
                  start: Math.floor(Math.min(mouseDownRef.current, target.x)),
                  end: Math.floor(Math.max(mouseDownRef.current, target.x)),
                  selectionBox: false,
                });
              }
            },
          },
        },
      },
    },
    xAxis: {
      type: 'logarithmic',
      crosshair: true,
      custom: {
        allowNegativeLog: true,
      },
      min: xValues[0],
    } as Highcharts.XAxisOptions,
    yAxis: {
      title: {
        text: '',
      },
      type: distributionYScale || K.Y_SCALE_OPTIONS.LINEAR,
      maxPadding: 0,
      endOnTick: false,
      minRange: 0.1,
      min: distributionYScale === K.Y_SCALE_OPTIONS.LOGARITHMIC ? 0.1 : 0,
    },
    series: [
      {
        type: 'column',
        pointWidth: 4,
        data,
        color: {
          linearGradient: {
            x1: 0,
            x2: 0,
            y1: 0,
            y2: 1,
          },
          stops: [
            [0, distributionGraphStyles.distributionSeriesColorStop0],
            [1, distributionGraphStyles.distributionSeriesColorStop1],
          ],
        },
      },
    ],
  });

  useEffect(() => {
    setChartOptions(
      cloneDeep({
        ...chartOptions,
        ...{
          yAxis: {
            type: distributionYScale || K.Y_SCALE_OPTIONS.LINEAR,
            min: distributionYScale === K.Y_SCALE_OPTIONS.LOGARITHMIC ? 0.1 : 0,
          },
        },
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [distributionYScale]);

  const prevXValues = usePrevious(xValues);
  const prevYValues = usePrevious(yValues);
  const prevHighchartsStyles = usePrevious(highchartsStyles);
  useConditionalEffect(() => {
    setChartOptions({
      ...chartOptions,
      series: [{ ...(chartOptions.series?.[0] as SeriesXrangeOptions), data }],
      xAxis: {
        ...chartOptions.xAxis,
        min: xValues[0],
      },
    });
  }, prevXValues !== xValues && yValues !== prevYValues);

  useConditionalEffect(() => {
    setChartOptions(cloneDeep({ ...chartOptions, ...highchartsStyles }));
  }, highchartsStyles !== prevHighchartsStyles);

  const prevChartRef = usePrevious(chartRef);
  useConditionalEffect(() => {
    setCurrentChartRef(chartRef);
  }, chartRef !== prevChartRef);
  if (xValues?.length !== yValues.length) {
    return null;
  }

  // If our chartRef had changed (or it is our first chartRef), add the ref to global state
  const handleMouseUp = (): void => {
    mouseDownRef.current = null;
  };
  const handleMouseDown = (e: PointerEventObject): void => {
    const currentChart = chartRef.current?.chart;
    if (currentChart) {
      mouseDownRef.current = currentChart.xAxis[0].toValue(e.chartX - currentChart.plotLeft, true);
    }
    if (selectionRange.selectionBox) {
      setSelectionRange({});
    }
  };

  return (
    <div className={className}>
      <ScalyrHighcharts
        ref={chartRef}
        highcharts={Highcharts}
        containerProps={{
          onMouseDown: (e: ContainerPropEvent): void => handleMouseDown(e.nativeEvent),
          onMouseUp: handleMouseUp,
          style: { height: '100%', width: '100%' },
        }}
        options={chartOptions}
      />
    </div>
  );
};

export default memo(DistributionGraph);
